// src/components/AddressInput.js
// Reusable address search field backed by GET /api/locations/autocomplete/?q=
// On selection it returns { description, lat, lng } to the parent via onSelect.

import React, { useRef, useEffect, useState } from 'react';
import useAddressAutocomplete from '../hooks/useAddressAutocomplete';

/**
 * Props:
 *   label       {string}   — field label
 *   placeholder {string}   — input placeholder
 *   value       {string}   — controlled display value (address text)
 *   onSelect    {function} — called with { description, lat, lng } when user picks a suggestion
 *   required    {boolean}
 *   disabled    {boolean}
 */
const AddressInput = ({ label, placeholder, value, onSelect, required = false, disabled = false }) => {
  const { query, setQuery, suggestions, loading } = useAddressAutocomplete(350, 2);
  const [open, setOpen] = useState(false);
  const [displayValue, setDisplayValue] = useState(value || '');
  const wrapperRef = useRef(null);

  // Sync external value changes (e.g. clear on form reset)
  useEffect(() => {
    setDisplayValue(value || '');
  }, [value]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setDisplayValue(val);
    setQuery(val);
    setOpen(true);
    // Clear parent selection when user types again
    if (onSelect) onSelect({ description: val, lat: null, lng: null });
  };

  const handleSelect = (s) => {
    const address = s.description;
    setDisplayValue(address);
    setQuery('');
    setOpen(false);
    if (onSelect) {
      onSelect({
        description: address,
        lat: s.lat ?? s.latitude ?? null,
        lng: s.lng ?? s.longitude ?? null,
      });
    }
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative', marginBottom: 14 }}>
      {label && (
        <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 4 }}>
          {label}{required && <span style={{ color: '#ef4444' }}> *</span>}
        </label>
      )}

      <div style={{ position: 'relative' }}>
        <input
          type="text"
          value={displayValue}
          onChange={handleChange}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          placeholder={placeholder || 'Search address...'}
          required={required}
          disabled={disabled}
          autoComplete="off"
          style={{
            width: '100%',
            padding: '10px 36px 10px 14px',
            border: '1px solid #d1d5db',
            borderRadius: 6,
            fontSize: 15,
            background: disabled ? '#f9fafb' : 'white',
            boxSizing: 'border-box',
            outline: 'none',
          }}
          onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}
        />
        {loading && (
          <span style={{
            position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
            fontSize: 14, color: '#9ca3af',
          }}>⏳</span>
        )}
      </div>

      {open && suggestions.length > 0 && (
        <ul style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000,
          background: 'white', border: '1px solid #d1d5db', borderRadius: 6,
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)', listStyle: 'none',
          margin: '2px 0 0', padding: 0, maxHeight: 240, overflowY: 'auto',
        }}>
          {suggestions.map((s) => (
            <li
              key={s.place_id || s.description}
              onMouseDown={() => handleSelect(s)}
              style={{
                padding: '10px 14px', cursor: 'pointer', fontSize: 14, color: '#111827',
                borderBottom: '1px solid #f3f4f6',
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#f0f9ff'}
              onMouseLeave={e => e.currentTarget.style.background = 'white'}
            >
              <span style={{ fontWeight: 500 }}>{s.main_text || s.description.split(',')[0]}</span>
              {s.secondary_text && (
                <span style={{ color: '#6b7280', fontSize: 12 }}> — {s.secondary_text}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AddressInput;
